import '../types/globals';

import { useCallback, useEffect } from 'react';

import { type AnalyticsEvent } from '../types';

type UseAnalyticsInterceptorProps = {
  isOpen: boolean;
  onEventCaptured: (event: AnalyticsEvent) => void;
};

function isValidObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export const useAnalyticsInterceptor = ({ isOpen, onEventCaptured }: UseAnalyticsInterceptorProps) => {
  const captureEvent = useCallback(
    (type: AnalyticsEvent['type'], args: unknown[]) => {
      try {
        const event: AnalyticsEvent = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          type,
          timestamp: new Date(),
        };

        // Safely parse arguments based on event type
        try {
          if (type === 'track' && args.length >= 1) {
            event.event = typeof args[0] === 'string' ? args[0] : String(args[0]);
            event.params = isValidObject(args[1]) ? args[1] : undefined;
            event.options = isValidObject(args[2]) ? args[2] : undefined;
          } else if (type === 'page' && args.length >= 2) {
            event.category = typeof args[0] === 'string' ? args[0] : String(args[0]);
            event.name = typeof args[1] === 'string' ? args[1] : String(args[1]);
            event.params = isValidObject(args[2]) ? args[2] : undefined;
            event.options = isValidObject(args[3]) ? args[3] : undefined;
          } else if (type === 'identify' && args.length >= 1) {
            event.userId = typeof args[0] === 'string' ? args[0] : String(args[0]);
            event.traits = isValidObject(args[1]) ? args[1] : undefined;
            event.options = isValidObject(args[2]) ? args[2] : undefined;
          } else if (type === 'alias' && args.length >= 1) {
            event.userId = typeof args[0] === 'string' ? args[0] : String(args[0]);
            event.params = isValidObject(args[1]) ? args[1] : undefined;
            event.options = isValidObject(args[2]) ? args[2] : undefined;
          }
        } catch (parseError) {
          console.warn('[DevModal] Error parsing analytics event args:', parseError);
          // Still capture the event with basic info
          event.params = { error: 'Failed to parse event arguments', args };
        }
        onEventCaptured(event);
      } catch (error) {
        console.error('[DevModal] Error capturing analytics event:', error);
      }
    },
    [onEventCaptured],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isOpen) return;

    // Set up global interceptor function for analytics hooks
    window.__devModalCaptureEvent = captureEvent;

    return () => {
      // Clean up global interceptor
      if (typeof window !== 'undefined' && window.__devModalCaptureEvent) {
        delete window.__devModalCaptureEvent;
      }
    };
  }, [isOpen, captureEvent]);
};
